/**
 * Copyright 2023 Design Barn Inc.
 */

'use client';

import { Button } from '@lottiefiles/ds-core';
import cn from 'classnames';
import React, { useCallback, useEffect } from 'react';
import type { ControllerRenderProps, UseFormStateReturn } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import Input from "../input/input";

export interface CodeFormInput {
  code: string;
}

export interface CodeFormProps {
  errors?: {
    code?: string;
  };
  formType?: string;
  mode: 'totp' | 'codes';
  onSubmit: (data: CodeFormInput) => Promise<void>;
  onUseCodes?: () => void;
  submitDisabled: boolean;
}

export default function CodeForm({
  errors,
  mode,
  onSubmit,
  onUseCodes,
  submitDisabled,
}: CodeFormProps): React.ReactElement {
  const {
    clearErrors,
    control,
    formState: { errors: formErrors, isDirty, isValid },
    handleSubmit,
    setError,
    setFocus,
  } = useForm<CodeFormInput>({
    defaultValues: {
      code: '',
    },
  });

  useEffect(() => {
    setFocus('code');
  }, [setFocus]);

  useEffect(() => {
    if (errors?.code || formErrors.code?.message) {
      setError('code', { message: errors?.code || formErrors.code?.message });
    } else {
      clearErrors('code');
    }

    if (!errors?.code) {
      clearErrors();
    }
  }, [clearErrors, errors?.code, formErrors.code?.message, setError]);

  const renderCodeField = useCallback(
    ({
      field,
      formState,
    }: {
      field: ControllerRenderProps<CodeFormInput, 'code'>;
      formState: UseFormStateReturn<CodeFormInput>;
    }): React.ReactElement => (
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...field}
        placeholder={mode === 'totp' ? 'Authentication code' : 'Backup code'}
        type="text"
        errorText={formState.errors.code?.message}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const controller = (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Controller
      name="code"
      control={control}
      render={renderCodeField}
      // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
      rules={{ required: 'Code is missing.' }}
    />
  );

  const handleUseCodes = useCallback(() => {
    onUseCodes?.();
  }, [onUseCodes]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form onSubmit={handleSubmit(onSubmit)}>
      <div>
        {controller}
        <Button
          id="button-code-submit"
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop, @typescript-eslint/no-empty-function
          onClick={(): void => {}}
          variant="primary"
          size="lg"
          type="submit"
          disabled={!isValid && !isDirty}
          loading={submitDisabled}
          className={cn("mt-4 w-full rounded-[12px] text-base font-normal", {
            'mb-4': mode !== 'totp',
          })}
        >
          {mode === 'totp' ? 'Verify code' : 'Log in with backup code'}
        </Button>

        {mode === 'totp' && (
          <Button
            id="button-backup-link"
            onClick={handleUseCodes}
            variant="ghost"
            size="md"
            type="button"
            className="my-4 w-full"
          >
            Use backup codes
          </Button>
        )}
      </div>
    </form>
  );
}
